import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import BillingNav from './BillingNav';
import { BillingPage } from './billingUi';
import { fetchPayments, formatMoney, paymentAction } from '../../services/billingApi';
import { toast } from 'react-toastify';

/** Payments list. Record a payment from an issued invoice. */
const BillingPayments = () => {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') || '';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await fetchPayments(tenantId || undefined));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (paymentId, action) => {
    try {
      await paymentAction(paymentId, action);
      toast.success(`Payment ${action.toLowerCase()}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <BillingPage>
      <BillingNav title="Payments" />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Recorded by</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary">
                    No payments. Record from an Issued invoice.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.paymentId}>
                  <TableCell>{String(r.paymentDate || '').slice(0, 10)}</TableCell>
                  <TableCell>{r.tenantName}</TableCell>
                  <TableCell>{r.invoiceNumber || '—'}</TableCell>
                  <TableCell>{r.paymentMethod || '—'}</TableCell>
                  <TableCell>{r.paymentReference || '—'}</TableCell>
                  <TableCell align="right">{formatMoney(r.amount, r.currency)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.paymentStatus} />
                  </TableCell>
                  <TableCell>{r.recordedBy || '—'}</TableCell>
                  <TableCell>
                    {r.paymentStatus === 'COMPLETED' && (
                      <Button size="small" onClick={() => act(r.paymentId, 'REFUND')}>Refund</Button>
                    )}
                    {r.paymentStatus === 'PENDING' && (
                      <Button size="small" onClick={() => act(r.paymentId, 'FAIL')}>Fail</Button>
                    )}
                    {!['VOID', 'REFUNDED'].includes(r.paymentStatus) && (
                      <Button size="small" color="warning" onClick={() => act(r.paymentId, 'VOID')}>Void</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </BillingPage>
  );
};

export default BillingPayments;
